import { useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { PaginationState } from "@tanstack/react-table";
import { useSearchParams } from "react-router";
import type { FilterValues, SortConfig } from "../../datagrid-configs/types";
import { buildTableUrlParams, readTableStateFromUrl } from "./tableUrlState";

const FILTER_SYNC_DEBOUNCE_MS = 300;

interface UseTableUrlSyncArgs {
	pagination: PaginationState;
	setPagination: Dispatch<SetStateAction<PaginationState>>;
	sort: SortConfig | undefined;
	setSort: Dispatch<SetStateAction<SortConfig | undefined>>;
	filterValues: FilterValues;
	setFilterValues: Dispatch<SetStateAction<FilterValues>>;
	enabled?: boolean;
}

function saveListUrlToSession(listPath: string, searchParams: URLSearchParams): void {
	const search = searchParams.toString();
	sessionStorage.setItem(`tableListUrl:${listPath}`, search === "" ? listPath : `${listPath}?${search}`);
}

function tableStateMatchesUrl(
	pagination: PaginationState,
	sort: SortConfig | undefined,
	filterValues: FilterValues,
	searchParams: URLSearchParams,
): boolean {
	const fromUrl = readTableStateFromUrl(searchParams);
	const pageSizeFromUrl = fromUrl.pageSizeFromUrl ?? pagination.pageSize;

	if (pagination.pageIndex !== fromUrl.pagination.pageIndex) {
		return false;
	}
	if (pagination.pageSize !== pageSizeFromUrl) {
		return false;
	}
	if (sort?.field !== fromUrl.sort?.field || sort?.direction !== fromUrl.sort?.direction) {
		return false;
	}

	return JSON.stringify(filterValues) === JSON.stringify(fromUrl.filterValues);
}

/**
 * Bidirectional sync between table state and URL search params.
 * Pagination and sort write immediately; filters are debounced.
 * Rehydrates state when the URL changes externally (browser back/forward).
 */
export function useTableUrlSync(args: UseTableUrlSyncArgs): void {
	const {
		pagination,
		setPagination,
		sort,
		setSort,
		filterValues,
		setFilterValues,
		enabled = true,
	} = args;

	const [searchParams, setSearchParams] = useSearchParams();
	const skipRehydrateFromUrl = useRef(false);
	const filterValuesJson = useRef(JSON.stringify(filterValues));
	const currentFilterValuesJson = JSON.stringify(filterValues);

	// Rehydrate React state when URL changes externally (e.g. browser back).
	useEffect(() => {
		if (enabled === false) {
			return;
		}

		if (skipRehydrateFromUrl.current) {
			skipRehydrateFromUrl.current = false;
			return;
		}

		if (tableStateMatchesUrl(pagination, sort, filterValues, searchParams)) {
			return;
		}

		const fromUrl = readTableStateFromUrl(searchParams);

		setPagination((previous) => {
			const nextPageSize = fromUrl.pageSizeFromUrl ?? previous.pageSize;
			if (previous.pageIndex === fromUrl.pagination.pageIndex && previous.pageSize === nextPageSize) {
				return previous;
			}
			return { pageIndex: fromUrl.pagination.pageIndex, pageSize: nextPageSize };
		});

		setSort(fromUrl.sort);

		setFilterValues((previous) => {
			if (JSON.stringify(previous) === JSON.stringify(fromUrl.filterValues)) {
				return previous;
			}
			return fromUrl.filterValues;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- rehydrate only when URL changes, not when local state catches up
	}, [enabled, searchParams]);

	// Write state to URL — pagination/sort immediately, filters debounced.
	useEffect(() => {
		if (enabled === false) {
			return;
		}

		const filtersChanged = currentFilterValuesJson !== filterValuesJson.current;
		filterValuesJson.current = currentFilterValuesJson;

		const writeToUrl = () => {
			const nextParams = buildTableUrlParams({ pagination, sort, filterValues });

			setSearchParams(
				(previous) => {
					const updated = new URLSearchParams(previous);
					for (const [key, value] of Object.entries(nextParams)) {
						if (value === null) {
							updated.delete(key);
						} else {
							updated.set(key, value);
						}
					}
					saveListUrlToSession(window.location.pathname, updated);
					return updated;
				},
				{ replace: true },
			);

			skipRehydrateFromUrl.current = true;
		};

		if (filtersChanged) {
			const timer = window.setTimeout(writeToUrl, FILTER_SYNC_DEBOUNCE_MS);
			return () => window.clearTimeout(timer);
		}

		writeToUrl();
		return undefined;
	}, [
		enabled,
		pagination.pageIndex,
		pagination.pageSize,
		sort?.field,
		sort?.direction,
		currentFilterValuesJson,
		setSearchParams,
	]);

	// Flush URL to sessionStorage on unmount so detail-page back links always have the latest list URL.
	useEffect(() => {
		if (enabled === false) {
			return;
		}

		return () => {
			const nextParams = buildTableUrlParams({ pagination, sort, filterValues });
			const updated = new URLSearchParams();
			for (const [key, value] of Object.entries(nextParams)) {
				if (value !== null) {
					updated.set(key, value);
				}
			}
			saveListUrlToSession(window.location.pathname, updated);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- capture latest state on unmount only
	}, [enabled, pagination.pageIndex, pagination.pageSize, sort?.field, sort?.direction, currentFilterValuesJson]);
}
